import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ALL_PLANS, type MeterView, PLAN_LABEL, type ScheduledSeatChange } from "@vd/api-client";
import { ChevronDown, Download } from "lucide-react";
import { type ReactNode, useState } from "react";
import { errorMessage } from "../../../api/client";
import { ledgerQuery, type NumberLedgerEntry } from "../../../api/numbers";
import { planKeys, planQuery, plans, statementsQuery } from "../../../api/plans";
import { requireAdmin } from "../../../auth/useMe";
import { toast } from "../../../components/toast";
import { Badge, Button, Card, ErrorState, Loading, PageHeader } from "../../../components/ui";
import { API_BASE } from "../../../config";
import { fmtNumber, fmtPhone, fmtUsd } from "../../../lib/format";
import { ledgerSummary, ledgerValue } from "../../../lib/numbers";
import { billingDay, meterShare, periodLine, seatCountText, seatLabel } from "../../../lib/seats";

export const Route = createFileRoute("/admin/billing/")({
  staticData: { crumb: { label: "Plan and seats", parents: [] } },
  beforeLoad: requireAdmin,
  loader: ({ context }) => context.queryClient.prefetchQuery(planQuery),
  component: PlanPage,
});

function Row({ label, children, sub }: { label: ReactNode; children: ReactNode; sub?: ReactNode }) {
  return (
    <li className="flex min-h-12 flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5">
      <div className="min-w-40 flex-1">
        <p className="text-ui">{label}</p>
        {sub ? <p className="text-caption text-ink-3">{sub}</p> : null}
      </div>
      <div className="flex items-center gap-3">{children}</div>
    </li>
  );
}

function amount(m: MeterView, n: number): string {
  return m.unit === "usd" ? fmtUsd(n) : fmtNumber(n);
}

function MeterRow({ m }: { m: MeterView }) {
  const limit = m.included + m.purchased;
  const share = meterShare(m.used, limit);
  const bar =
    m.state === "reached"
      ? m.hard_cap
        ? "bg-danger"
        : "bg-warning"
      : m.state === "warning"
        ? "bg-warning"
        : "bg-ink-2";
  return (
    <Row label={m.label} sub={m.purchased > 0 ? `${amount(m, m.purchased)} purchased` : undefined}>
      {m.hard_cap ? <Badge>Hard cap</Badge> : null}
      <span className="w-40 text-right text-ui tnum">
        {amount(m, m.used)} <span className="text-ink-3">of {amount(m, limit)}</span>
      </span>
      <meter className="sr-only" aria-label={`${m.label} used`} min={0} max={1} value={share} />
      <span className="relative h-1.5 w-32 rounded-pill bg-fill-2" aria-hidden>
        <span
          className={`absolute inset-y-0 left-0 rounded-pill ${bar}`}
          style={{ width: `${share * 100}%` }}
        />
      </span>
    </Row>
  );
}

/** Numbers this month: "3 extra, $12.00 this month", the ledger rows behind a disclosure. */
function NumbersRow({ entries }: { entries: NumberLedgerEntry[] }) {
  const [open, setOpen] = useState(false);
  const charges = entries.filter((e) => e.kind === "charge");
  return (
    <li>
      <div className="flex min-h-12 flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5">
        <p className="min-w-40 flex-1 text-ui">Numbers</p>
        <button
          type="button"
          aria-expanded={open}
          aria-controls="numbers-ledger"
          onClick={() => setOpen((o) => !o)}
          className="inline-flex items-center gap-1.5 rounded-md text-ui tnum hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--vd-focus)] focus-visible:outline-none"
        >
          {ledgerSummary(entries)}
          <ChevronDown
            aria-hidden
            className={`size-4 text-ink-3 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>
      {open ? (
        <ul id="numbers-ledger" className="border-hairline border-t pb-1">
          {charges.map((e, i) => (
            // Entries carry no id; one number has one charge a month, the index keeps it unique.
            // biome-ignore lint/suspicious/noArrayIndexKey: the list is fixed for the month
            <li key={`${i}-${e.e164}`} className="flex h-9 items-center justify-between px-4 text-ui">
              <span className="tnum">{fmtPhone(e.e164)}</span>
              <span className={e.included || e.biller === "carrier" ? "text-ink-2" : "tnum"}>
                {ledgerValue(e)}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function ScheduledRow({ c }: { c: ScheduledSeatChange }) {
  const qc = useQueryClient();
  const cancel = useMutation({
    mutationFn: () => plans.cancelChange(c.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: planKeys.plan });
      toast(`${c.name} keeps ${seatLabel(c.from, c.from_marketing)}.`);
    },
    onError: (e) => toast(errorMessage(e), "danger"),
  });
  return (
    <Row
      label={c.name}
      sub={`${seatLabel(c.from, c.from_marketing)} to ${seatLabel(c.to, c.to_marketing)}, ${billingDay(c.effective_at)}`}
    >
      <Button size="sm" busy={cancel.isPending} onClick={() => cancel.mutate()}>
        Cancel
      </Button>
    </Row>
  );
}

function PlanPage() {
  const plan = useQuery(planQuery);
  const statements = useQuery(statementsQuery);
  const ledger = useQuery(ledgerQuery);
  if (plan.isPending) return <Loading />;
  if (plan.isError) return <ErrorState error={plan.error} onRetry={() => plan.refetch()} />;
  const p = plan.data;
  return (
    <>
      <PageHeader title="Plan and seats">{periodLine(p.status, p.period_start, p.period_end)}</PageHeader>
      <div className="flex max-w-[880px] flex-col gap-4">
        <Card title="Seats">
          <ul className="divide-y divide-hairline">
            {ALL_PLANS.map((plan) => {
              const c = p.seats.find((s) => s.plan === plan) ?? { plan, purchased: null, assigned: 0 };
              const full = c.purchased !== null && c.assigned >= c.purchased;
              return (
                <Row key={plan} label={PLAN_LABEL[plan]}>
                  {full ? <Badge tone="warn">Full</Badge> : null}
                  <span className="text-ui tnum">
                    {c.purchased === null ? `${c.assigned} assigned, no cap` : seatCountText(c)}
                  </span>
                </Row>
              );
            })}
          </ul>
        </Card>
        {p.scheduled.length ? (
          <Card title="Scheduled changes">
            <ul className="divide-y divide-hairline">
              {p.scheduled.map((c) => (
                <ScheduledRow key={c.id} c={c} />
              ))}
            </ul>
          </Card>
        ) : null}
        <Card title="Usage this period">
          <ul className="divide-y divide-hairline">
            {p.meters.map((m) => (
              <MeterRow key={m.meter} m={m} />
            ))}
            {ledger.data?.entries.some((e) => e.kind === "charge") ? (
              <NumbersRow entries={ledger.data.entries} />
            ) : null}
          </ul>
        </Card>
        <Card title="Statements">
          {statements.isPending ? (
            <Loading />
          ) : statements.isError ? (
            <ErrorState error={statements.error} onRetry={() => statements.refetch()} />
          ) : statements.data.statements.length === 0 ? (
            <p className="px-4 py-3 text-ui text-ink-3">No statements yet</p>
          ) : (
            <ul className="divide-y divide-hairline">
              {statements.data.statements.map((s) => (
                <Row key={s.id} label={`${billingDay(s.period_start)} to ${billingDay(s.period_end)}`}>
                  <span className="text-ui tnum">{fmtUsd(s.total_usd)}</span>
                  <Badge tone={s.state === "draft" ? "neutral" : "ok"}>
                    {s.state === "draft" ? "Draft" : s.state === "sent" ? "Sent" : "Final"}
                  </Badge>
                  <a
                    className="inline-flex size-8 items-center justify-center rounded-md text-ink-2 hover:bg-fill-2 focus-visible:outline-2 focus-visible:outline-focus"
                    href={`${API_BASE}${plans.exportUrl(s.id)}`}
                    aria-label={`Download the statement for ${billingDay(s.period_start)} as CSV`}
                  >
                    <Download className="size-4" />
                  </a>
                </Row>
              ))}
            </ul>
          )}
        </Card>
        <Card title="Billing">
          <ul className="divide-y divide-hairline">
            <Row label="Payment">
              <span className="text-ui text-ink-3">{p.billing.provider ?? "Not set up"}</span>
            </Row>
          </ul>
        </Card>
      </div>
    </>
  );
}
